import { HttpHeaders, HttpParams } from '@angular/common/http';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { CanActivateFn, Params } from '@angular/router';
import { format, isDate, isValid, parseISO } from 'date-fns';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  last,
  MonoTypeOperatorFunction,
  Observable,
  of,
  Subject,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import {
  API_DATETIME_FORMAT,
  CoreErrorCodes,
  IDropdownItem,
  InternalError,
  IUpdateQueryParamWithoutNavigationOptions,
  IWaitForElementOptions,
  Language,
} from 'src/app/core/contracts';
import { environment } from 'src/environments/environment';
import { environments } from 'src/environments/environments';

export const isDev = environment.name === environments.dev;
export const isTesting = environment.name === environments.staging;
export const isStaging = environment.name === environments.staging;
export const isProd = environment.name === environments.production;
export const isArabic = (language: Language) => language === Language.ar;

export function sequentialGuards(...guards: CanActivateFn[]): CanActivateFn {
  return (route, state) => {
    if (!guards.length) {
      return of(true);
    }
    const environmentInjector = inject(EnvironmentInjector);
    // Convert the array of guards into an observable sequence.
    return from(guards).pipe(
      // Execute each guard sequentially, preserving DI context.
      concatMap(guard =>
        runInInjectionContext(environmentInjector, () => {
          const guardResult = guard(route, state);
          if (guardResult instanceof Observable) {
            return guardResult;
          }
          if (guardResult instanceof Promise) {
            return from(guardResult);
          }
          return of(guardResult);
        })
      ),
      // Short‑circuit: stop once result is not strictly true (but include that last value).
      takeWhile(value => value === true, true),
      // If every guard returned EMPTY (no emissions), treat as a failed guard (false).
      defaultIfEmpty(false),
      // Emit the final (last) guard result.
      last()
    );
  };
}

/**
 * Remove query parameters from the current route without triggering a navigation event.
 * @param router The Angular Router instance.
 * @param route The current ActivatedRoute instance.
 * @param location The Location service instance.
 * @param param The query parameter(s) to remove.
 */
export function removeQueryParamWithoutEvent(options: IUpdateQueryParamWithoutNavigationOptions) {
  const { router, route, location, params } = options;
  const current = { ...route.snapshot.queryParams };
  Object.keys(params).forEach(p => delete current[p]);

  const tree = router.createUrlTree([], {
    relativeTo: route,
    queryParams: current,
    queryParamsHandling: '', // use exactly the params we pass
    preserveFragment: true, // keep #fragment if present
  });

  const newUrl = router.serializeUrl(tree);
  location.replaceState(newUrl); // no new history entry; use go(newUrl) to add one
}

/**
 * Adds or updates query parameters in the current route without triggering a navigation event.
 * @param options The options for updating query parameters.
 */
export function addQueryParamWithoutEvent(options: IUpdateQueryParamWithoutNavigationOptions) {
  const { router, route, location, params, addHistoryEntry = false, preserveFragment = true } = options;

  const current: Params = { ...route.snapshot.queryParams, ...params };

  const tree = router.createUrlTree([], {
    relativeTo: route,
    queryParams: current,
    queryParamsHandling: '', // use exactly what we pass
    preserveFragment, // keep #fragment if present
  });

  const url = router.serializeUrl(tree);
  if (addHistoryEntry) {
    location.go(url);
  } else {
    location.replaceState(url);
  }
}

/**
 * Turns any of these…
 * "https://example.com/tabs/home;id=7?x=1#top", "//example.com/tabs/home", "/tabs/home;id=7", "tabs/home;id=7?x=1#top"
 * into a string that Angular's Router can safely parse: "/tabs/home;id=7?x=1#top"
 */
export function toRouterPath(raw: string): string {
  // Remove protocol + host when present
  if (/^[a-z][\w+.-]*:\/\//i.test(raw) || raw.startsWith('//')) {
    const u = new URL(raw);
    raw = u.pathname + u.search + u.hash; // keeps ;matrix ?query #fragment
  }

  // Ensure a leading slash (router expects it)
  if (!raw.startsWith('/')) {
    raw = '/' + raw;
  }

  return raw;
}

export function toNumberArrayTransformer(v: string | string[] | null): number[] {
  if (v == null) {
    return [];
  }
  return (Array.isArray(v) ? v : [v]).map(Number).filter(n => !Number.isNaN(n));
}

/**
 * Reverse of `encodeQueryParam`.
 * • Missing / empty / malformed → `null`.
 * • Generic <T> lets the caller specify the expected shape.
 */
export function decodeQueryParam<T>(raw?: string): T | null {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(atob(raw))) as T;
  } catch {
    return null;
  }
}

/**
 * Serialize an object (or array / value) into a compact, URL-safe Base64 string.
 * Returns `undefined` for null / undefined so the key is skipped in queryParams.
 */
export function encodeQueryParam<T>(data: T | null | undefined): string | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }
  return btoa(encodeURIComponent(JSON.stringify(data)));
}

/**
 * pushTo
 * -------
 * Bridge a **cold** observable into a **hot** Subject/BehaviorSubject by forwarding only `next`
 * notifications, while *ignoring* `error` and `complete` (by default).
 *
 * ✅ Use when:
 *   - You load data from an api and need to push the results into hot observable that's used in the view.
 *
 * @param dest The subject to receive values.
 * @param defaultValue Value (or factory) to push if an error occurs.
 * @returns MonoTypeOperatorFunction<T>
 *
 * @example
 * // Basic: HTTP cold stream into a BehaviorSubject
 * const users$ = this.http.get<User[]>('/api/users');
 * const usersState$ = new BehaviorSubject<User[]>([]);
 * users$.pipe(pushTo(usersState$)).subscribe();
 *
 * @example
 * // Let errors bubble so you can handle upstream
 * cold$.pipe(pushTo(state$, [])).subscribe();
 *
 * @example
 * // Angular destroy-aware
 * cold$
 *   .pipe(pushTo(state$), takeUntilDestroyed(this.destroyRef))
 *   .subscribe();
 */
export function pushTo<T>(dest: Subject<T>, defaultValue?: T | (() => T)): MonoTypeOperatorFunction<T> {
  return source =>
    source.pipe(
      tap(v => dest.next(v)),
      catchError(err => {
        const fallbackProvided = defaultValue !== undefined;
        let toEmit: T | undefined;

        if (fallbackProvided) {
          toEmit = typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
        } else {
          // Try to infer from BehaviorSubject.current value
          const hasValueProp = 'value' in dest; // BehaviorSubject check
          if (hasValueProp) {
            const current: unknown = (dest as BehaviorSubject<T>).value;
            if (Array.isArray(current)) {
              toEmit = [] as unknown as T;
            } else if (typeof current === 'number' || typeof current === 'string' || (current !== null && typeof current === 'object')) {
              toEmit = null as unknown as T;
            } else {
              toEmit = null as unknown as T;
            }
          }
        }

        if (toEmit !== undefined) {
          dest.next(toEmit);
        }

        return throwError(() => err as unknown);
      })
    );
}

// -----------------------------------------------------------------------------
// HTTP headers
// -----------------------------------------------------------------------------
export const JSON_HEADER = new HttpHeaders({ Accept: 'application/json' });

export const FORM_URLENCODED_HEADER = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

/**
 * NOTE: Usually you should NOT set multipart boundary yourself; browser will.
 * Keep only if your backend truly requires this header.
 */
export const MULTIPART_HEADER = new HttpHeaders({ 'Content-Type': 'multipart/form-data' });

// -----------------------------------------------------------------------------
// Query-string / params helpers
// -----------------------------------------------------------------------------
// TODO:tweak remove all calls to `toHttpParams` and let the api.ts auto convert params if any
export function toHttpParams(object: unknown): HttpParams | null {
  // supports nested objects and arrays
  if (!object) {
    return null;
  }

  let params = new HttpParams();

  const appendParam = (key: string, value: unknown): void => {
    if (value == null) {
      return;
    }

    if (isDate(value)) {
      params = params.append(key, format(value, API_DATETIME_FORMAT));
      // toISOString strips timezone info (UTC time) (e.g. produce a date in the past -3 hours)
      // params = params.set(key, (property as Date).toISOString());
    } else if (typeof value === 'string' || value instanceof String) {
      params = params.append(key, value.toString());
    } else if (typeof value === 'number' && !isNaN(value)) {
      params = params.append(key, value.toString());
    } else if (typeof value === 'boolean') {
      params = params.append(key, value.toString());
    } else if (Array.isArray(value)) {
      // Handle arrays - .NET Core v6 expects array format like: key[0], key[1], etc.
      value.forEach((item, index) => appendParam(`${key}[${index}]`, item));
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects - .NET Core v6 expects dot notation like: key.property
      Object.keys(value).forEach(subKey => {
        const subValue = (value as Record<string, unknown>)[subKey];
        appendParam(`${key}.${subKey}`, subValue);
      });
    } else {
      // Fallback: try to JSON stringify
      try {
        params = params.append(key, JSON.stringify(value));
        /* if (typeof value === 'object' && value !== null) {
          const stringValue = JSON.stringify(value);
          params = params.append(key, stringValue);
        } else {
          params = params.append(key, String(value));
        } */
      } catch (error) {
        console.warn(`@toHttpParams(): Cannot serialize value for key "${key}"`, value, error);
        // Skip this parameter rather than throwing an error
      }
    }
  };

  Object.keys(object as Record<string, unknown>).forEach(key => {
    const property = (object as Record<string, unknown>)[key];
    appendParam(key, property);
  });

  return params;
}

// -----------------------------------------------------------------------------
// Date formatting within objects
// -----------------------------------------------------------------------------
export function formatDateProperties(obj: unknown): unknown {
  // NOTE it won't traverse properties if obj is of type FormData
  const traverse = (obj: unknown) => {
    if (obj == null) {
      return;
    }
    for (const [key, value] of Object.entries(obj)) {
      if (value == null) {
        continue;
      }
      if (isDate(value)) {
        (obj as Record<string, unknown>)[key] = format(value, API_DATETIME_FORMAT);
      } else if (Array.isArray(value)) {
        value.forEach(item => traverse(item));
      } else if (value instanceof FormData) {
        console.warn(new InternalError('Cannot format date properties in FormData', CoreErrorCodes.InvalidState, true, obj));
      } else if (typeof value === 'object') {
        traverse(value);
      }
    }
  };
  traverse(obj);
  return obj;
}

// -----------------------------------------------------------------------------
// FormData conversion
// -----------------------------------------------------------------------------
export function toFormData(object: unknown, parentKey = ''): FormData {
  const formData = new FormData();

  const walk = (obj: unknown, baseKey: string) => {
    if (obj == null) return;

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const key = `${baseKey}[${index}]`;
        if (item instanceof Blob) {
          formData.append(baseKey, item);
        } else if (item instanceof Date) {
          formData.append(key, format(item, API_DATETIME_FORMAT));
        } else if (typeof item === 'object') {
          walk(item, key);
        } else {
          formData.append(key, item as string);
        }
      });
      return;
    }

    if (obj instanceof Date) {
      formData.append(baseKey, format(obj, API_DATETIME_FORMAT));
      return;
    }

    if (typeof obj === 'object' && !(obj instanceof Blob)) {
      Object.keys(obj as Record<string, unknown>).forEach(childKey => {
        const fullKey = baseKey ? `${baseKey}[${childKey}]` : childKey;
        walk((obj as Record<string, unknown>)[childKey], fullKey);
      });
      return;
    }

    formData.append(baseKey, obj as string | Blob);
  };

  walk(object, parentKey);
  return formData;
}

// -----------------------------------------------------------------------------
// Validators / Misc
// -----------------------------------------------------------------------------
export function isDateString(value: string): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  // Assuming the string is in ISO format (e.g., "2021-08-30", "2016-11-20T09:20:01.36")
  const parsedDate = parseISO(value);
  return isValid(parsedDate);
}

export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function getDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const deg2rad = 0.017453292519943295; // === Math.PI / 180
  const cos = Math.cos;
  lat1 *= deg2rad;
  lon1 *= deg2rad;
  lat2 *= deg2rad;
  lon2 *= deg2rad;
  const a = (1 - cos(lat2 - lat1) + (1 - cos(lon2 - lon1)) * cos(lat1) * cos(lat2)) / 2;

  return 12742 * Math.asin(Math.sqrt(a)); // Diameter of the earth in km (2 * 6371)
}

export const containsArabic = (text: string): boolean => /[\u0600-\u06FF]/.test(text);

export const isPhoneNumber = (phone: string): boolean => !!phone && /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s./0-9]*$/g.test(phone);

export const isNumber = (value: string | number): boolean => /^\d+$/.test(value.toString());

export const isDataURL = (value: string): boolean =>
  /^\s*data:([a-z]+\/[a-z*]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+;=\-._~:@/?%\s]*\s*$/i.test(value);

export const isBase64 = (data: string): boolean => /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/.test(data);

export function isBase64Image(data: string): boolean {
  const len = data.length;

  // Length must be multiple of 4
  if ((len & 3) !== 0) return false;

  let padCount = 0;
  let seenPad = false;

  for (let i = 0; i < len; i++) {
    const c = data.charCodeAt(i);

    // '=' padding
    if (c === 0x3d) {
      // '='
      seenPad = true;
      padCount++;
      if (padCount > 2) return false;
      continue;
    }

    // Any char after padding is illegal
    if (seenPad) return false;

    // Check allowed Base64 chars: A–Z, a–z, 0–9, +, /
    if (
      !(
        (
          (c >= 0x41 && c <= 0x5a) || // A–Z
          (c >= 0x61 && c <= 0x7a) || // a–z
          (c >= 0x30 && c <= 0x39) || // 0–9
          c === 0x2b || // '+'
          c === 0x2f
        ) // '/'
      )
    ) {
      return false;
    }
  }

  return true;
}

export function getExtension(file: string | File): string {
  const name = typeof file === 'string' ? file : file.name;
  return name.substring((~-name.lastIndexOf('.') >>> 0) + 2).toLowerCase();
}

export function getFileName(file: string | File): string {
  const name = typeof file === 'string' ? file : file.name;
  return name.substring(name.lastIndexOf('/') + 1);
}

export const getPath = (filePath: string): string => filePath.substring(0, filePath.lastIndexOf('/') + 1);

export function normalizeFileName(name: string): string {
  name = name.replace(/[|&;$%@" \-<>()+,]/g, ''); // remove illegal chars
  name = name.replace(/\.(?=.*?\.)/, ''); // remove dots except the extension dot
  return name;
}

export function enumToDropdown<T extends Record<string, string | number>>(e: T): IDropdownItem<T[keyof T]>[] {
  return Object.keys(e)
    .filter(i => Number.isNaN(+i)) // drop reverse numeric keys like "1", "2", ...
    .map(i => ({
      text: i,
      value: e[i as keyof T],
    }));
}

export function getHash(value: string): number {
  let hash = 0;
  if (!value.length) return hash;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0; // Convert to 32bit int
  }
  return hash;
}

// -----------------------------------------------------------------------------
// DOM helpers
// -----------------------------------------------------------------------------
export function getElements(options: IWaitForElementOptions): Element[] {
  const host = options.host || document;
  return Array.from(
    options.checkInShadow ? (host as HTMLElement).shadowRoot?.querySelectorAll(options.selector) ?? [] : host.querySelectorAll(options.selector)
  );
}

export function waitForElementViaObserver(options: IWaitForElementOptions): Promise<Element[]> {
  const defaultOptions: IWaitForElementOptions = { selector: '', host: document, checkInShadow: false, timeout: 15000 };
  options = { ...defaultOptions, ...options };

  return new Promise<Element[]>(resolve => {
    const elements = getElements(options);
    if (elements.length) {
      resolve(elements);
      return;
    }

    const timeoutId = setTimeout(() => {
      observer.disconnect();
      resolve([]);
    }, options.timeout);

    const observeTarget = options.checkInShadow ? (options.host as HTMLElement).shadowRoot : options.host;
    const observer = new MutationObserver(() => {
      const els = getElements(options);
      if (els.length) {
        resolve(els);
        observer.disconnect();
        clearTimeout(timeoutId);
      }
    });

    if (observeTarget) {
      observer.observe(observeTarget, { childList: true, subtree: true });
    } else {
      // eslint-disable-next-line no-console
      console.error('@waitForElementViaObserver: observeTarget is undefined');
    }
  });
}

export async function waitForElement(options: IWaitForElementOptions): Promise<Element | null> {
  const defaultOptions: IWaitForElementOptions = { selector: '', host: document, checkInShadow: false, timeout: 15000 };
  const start = Date.now();
  options = { ...defaultOptions, ...options };

  try {
    while (Date.now() - start < (options.timeout ?? 15000)) {
      const element = getElements(options)[0];
      if (element) {
        return element;
      }
      await delay(500);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('@waitForElement', error);
  }
  return null;
}

export function waitForShadowSelector<T extends HTMLElement>(host: HTMLElement, selector: string, timeout = 2000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const tryFind = () => host.shadowRoot?.querySelector<T>(selector) ?? null;
    const found = tryFind();
    if (found) {
      resolve(found);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = tryFind();
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });

    if (host.shadowRoot) {
      observer.observe(host.shadowRoot, { childList: true, subtree: true });
    } else {
      // eslint-disable-next-line no-console
      console.error('@waitForShadowSelector: cannot wait for element, no shadow dom, host.shadowRoot is undefined, host:', host);
    }

    setTimeout(() => {
      observer.disconnect();
      reject(`Timed out waiting for ${selector}`);
    }, timeout);
  });
}

// -----------------------------------------------------------------------------
// CSS helper
// -----------------------------------------------------------------------------
export function toCssSimple(obj: Record<string, string | number>): string {
  return Object.entries(obj)
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
}
